import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from '../../environments/environment';
import {
  AssignClothingInput,
  CreateClothingInput,
  CreateClothingOutput,
  CreateClothingResult,
  GetAllClothingInput,
  GetClothingHistoryOutput,
  GetClothingOutput,
  PatchClothingInput,
  PatchClothingOutput,
} from './clothing-api.types';
import { GetMatchcodesOutput } from './provision-request-api.types';

@Injectable({ providedIn: 'root' })
export class ClothingApiService {
  private resource = `${environment.api.avs}/Kleidung`;

  constructor(
    private http: HttpClient,
    private message: NzMessageService,
  ) {}

  getClothing(id: number): Promise<GetClothingOutput | null> {
    const url = `${this.resource}/${id}`;

    return firstValueFrom(
      this.http.get<GetClothingOutput>(url).pipe(
        catchError((error: HttpErrorResponse) => {
          this.message.error(
            `Fehler beim Abrufen der Kleidung: ${error.message}`,
          );
          return of(null);
        }),
      ),
    );
  }

  getAllClothing(input: GetAllClothingInput): Observable<GetClothingOutput[]> {
    const url = `${this.resource}`;
    const params = new HttpParams({ fromObject: input });

    return this.http.get<GetClothingOutput[]>(url, { params }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of([]); // Return empty array instead of throwing an 404 error
        } else {
          this.message.error(
            'Etwas beim Laden der Kleidung ist schiefgelaufen',
          );
          return of([]);
        }
      }),
    );
  }

  getClothingSizes(): Promise<GetMatchcodesOutput[]> {
    const url = `${environment.api.avs}/Matchcode/kleidergroesse`;

    return firstValueFrom(this.http.get<GetMatchcodesOutput[]>(url));
  }

  getClothingType(): Promise<GetMatchcodesOutput[]> {
    const url = `${environment.api.avs}/Matchcode/kleidungsart`;

    return firstValueFrom(this.http.get<GetMatchcodesOutput[]>(url));
  }

  getClothingTypes(): Promise<GetMatchcodesOutput[]> {
    const url = `${environment.api.avs}/Matchcode/kleidungsart`;

    return firstValueFrom(this.http.get<GetMatchcodesOutput[]>(url));
  }

  patchClothing(
    id: number,
    input: PatchClothingInput,
  ): Promise<PatchClothingOutput> {
    const url = `${this.resource}/${id}`;

    return firstValueFrom(this.http.patch<PatchClothingOutput>(url, input));
  }

  deleteClothing(id: number): Promise<void> {
    const url = `${this.resource}/${id}`;

    return firstValueFrom(this.http.delete<void>(url));
  }

  assignClothing(input: AssignClothingInput): Promise<GetClothingOutput[]> {
    const url = `${this.resource}/zuweisen`;

    return firstValueFrom(this.http.patch<GetClothingOutput[]>(url, input));
  }

  withdrawClothing(id: number): Promise<GetClothingOutput | null> {
    const url = `${this.resource}/${id}/zuruecknehmen`;

    return firstValueFrom(
      this.http.patch<GetClothingOutput>(url, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.message.error('Fehler beim Aktualisieren der Kleidung.');
          return of(null);
        }),
      ),
    );
  }

  launderClothing(id: number): Promise<GetClothingOutput | null> {
    const url = `${this.resource}/${id}/waschen`;

    return firstValueFrom(
      this.http.patch<GetClothingOutput>(url, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.message.error('Fehler beim Aktualisieren der Kleidung.');
          return of(null);
        }),
      ),
    );
  }

  damageClothing(id: number): Promise<GetClothingOutput> {
    const url = `${this.resource}/${id}/beschaedigt`;

    return firstValueFrom(this.http.patch<GetClothingOutput>(url, {}));
  }

  getClothingHistory(id: number): Promise<GetClothingHistoryOutput> {
    const url = `${this.resource}/${id}/Historie`;

    return firstValueFrom(this.http.get<GetClothingHistoryOutput>(url));
  }

  createNewClothing(
    newClothing: CreateClothingInput,
  ): Promise<CreateClothingResult> {
    const url = `${this.resource}`;

    return firstValueFrom(
      this.http.post<CreateClothingOutput>(url, newClothing, {}).pipe(
        map(
          (clothing): CreateClothingResult => ({ status: 'created', clothing }),
        ),
        catchError(
          (error: HttpErrorResponse): Observable<CreateClothingResult> => {
            if (error.status === 409) {
              return of({
                status: 'conflict',
                message:
                  this.extractErrorMessage(error) ??
                  'Die Inventarnummer existiert bereits.',
              });
            }

            this.message.error(this.createErrorMessage(error));
            return of({ status: 'error' });
          },
        ),
      ),
    );
  }
  findClothingByBarcode(
    inventarnummer: string,
  ): Promise<GetClothingOutput[] | null> {
    const url = `${this.resource}`;
    const params = new HttpParams({ fromObject: { inventarnummer } });

    return firstValueFrom(
      this.http.get<GetClothingOutput[]>(url, { params }).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of([]);
          }

          console.error(error);
          return of(null);
        }),
      ),
    );
  }

  private extractErrorMessage(error: HttpErrorResponse): string | null {
    const body = error.error;

    if (typeof body === 'string' && body.trim().length > 0) {
      return body.trim();
    }

    return body?.detail ?? body?.message ?? body?.title ?? null;
  }

  private createErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 0:
        return 'Keine Verbindung zum Server. Bitte Netzwerk prüfen.';
      case 400:
        return (
          this.extractErrorMessage(error) ??
          'Die Eingaben wurden vom Server abgelehnt.'
        );
      case 401:
      case 403:
        return 'Keine Berechtigung zum Anlegen eines Kleidungsstücks.';
      default:
        return (
          this.extractErrorMessage(error) ??
          'Das Kleidungsstück konnte nicht angelegt werden.'
        );
    }
  }
}
